import {createExerciseModal} from "./exerciseModal";
import {findPrefillResponse} from "./exercisePickerShared";
import {setupTabs} from "./setupTabs";

const unitDefinitions = {
    kg: {step: 0.1, remainderSymbol: null, remainderMultiple: null},
    lb: {step: 0.1, remainderSymbol: null, remainderMultiple: null},
    st: {step: 0.1, remainderSymbol: "lb", remainderMultiple: 14},
};
const storageKey = "strength_symmetry_last_submit";

function isBodyweightCategory(category) {
    return category === "Bodyweight";
}

function findExerciseById(exercises, exerciseId) {
    const id = String(exerciseId || "");
    for (let i = 0; i < exercises.length; i++) {
        if (String(exercises[i].id) === id) {
            return exercises[i];
        }
    }
    return null;
}

function setExercise(row, exercise) {
    const hidden = row.querySelector("[data-symmetry-exercise-hidden]");
    const label = row.querySelector("[data-symmetry-exercise-name]");
    const exerciseName = exercise.name || "";
    const exerciseCategory = exercise.category || "";

    if (hidden) {
        hidden.value = exercise.id;
    }
    if (label) {
        label.textContent = exerciseName;
    }
    row.dataset.symmetryExerciseCategory = exerciseCategory;
    updateRowForExercise(row);
}

function updateRowForExercise(row) {
    const isBodyweight = isBodyweightCategory(row.dataset.symmetryExerciseCategory || "");
    const weightContainer = row.querySelector("[data-symmetry-weight-container]");
    const weightInput = row.querySelector("[data-symmetry-weight]");
    const unitSelect = row.querySelector("[data-symmetry-weight-unit]");

    if (weightContainer) {
        weightContainer.classList.toggle("is-hidden", isBodyweight);
    }
    if (weightInput) {
        weightInput.disabled = isBodyweight;
        if (isBodyweight) {
            weightInput.value = "";
        }
    }
    if (unitSelect) {
        unitSelect.disabled = isBodyweight;
    }
}

function enhanceRow(row, exercises) {
    const hidden = row.querySelector("[data-symmetry-exercise-hidden]");
    const label = row.querySelector("[data-symmetry-exercise-name]");

    if (!hidden || !label) {
        return;
    }

    if (!label.textContent) {
        const exercise = findExerciseById(exercises, hidden.value);
        if (exercise) {
            setExercise(row, exercise);
            return;
        }
        label.textContent = "";
    }
    updateRowForExercise(row);
}

function enhanceRows(rows, exercises) {
    const liftRows = rows.querySelectorAll("[data-lift-row]");
    for (let i = 0; i < liftRows.length; i++) {
        enhanceRow(liftRows[i], exercises);
    }
}

function getRows(rows) {
    return Array.prototype.slice.call(rows.querySelectorAll("[data-lift-row]"));
}

function hasAnyLiftWeight(rows) {
    const liftRows = getRows(rows);
    for (let i = 0; i < liftRows.length; i++) {
        const input = liftRows[i].querySelector("[data-symmetry-weight]");
        if (input && input.value !== "") {
            return true;
        }
    }
    return false;
}

function hasExerciseRow(rows, exerciseId) {
    const id = String(exerciseId || "");
    if (!id) {
        return false;
    }
    const liftRows = getRows(rows);
    for (let i = 0; i < liftRows.length; i++) {
        const hidden = liftRows[i].querySelector("[data-symmetry-exercise-hidden]");
        if (hidden && String(hidden.value) === id) {
            return true;
        }
    }
    return false;
}

function getPageLiftUnit(rows, bodyweightUnitSelect) {
    const liftRows = getRows(rows);
    for (let i = 0; i < liftRows.length; i++) {
        const unitSelect = liftRows[i].querySelector("[data-symmetry-weight-unit]");
        if (unitSelect && (unitSelect.value === "kg" || unitSelect.value === "lb")) {
            return unitSelect.value;
        }
    }
    return bodyweightUnitSelect && bodyweightUnitSelect.value === "kg" ? "kg" : "lb";
}

function setAllWeightUnits(rows, unit) {
    const liftRows = getRows(rows);
    for (let i = 0; i < liftRows.length; i++) {
        const unitSelect = liftRows[i].querySelector("[data-symmetry-weight-unit]");
        if (unitSelect) {
            unitSelect.value = unit;
        }
    }
}

function getInputValue(root, selector) {
    const input = root.querySelector(selector);
    return input ? input.value || "" : "";
}

function setInputValue(root, selector, value) {
    const input = root.querySelector(selector);
    if (input) {
        input.value = value || "";
    }
}

function setSelectValue(root, selector, value) {
    const select = root.querySelector(selector);
    if (!select) {
        return;
    }
    for (let i = 0; i < select.options.length; i++) {
        if (select.options[i].value === value) {
            select.value = value;
            return;
        }
    }
    throw new Error("Invalid select value");
}

function validateString(value) {
    if (typeof value !== "string") {
        throw new Error("Invalid stored form field");
    }
    return value;
}

function validateEnum(value, allowedValues) {
    validateString(value);
    if (allowedValues.indexOf(value) === -1) {
        throw new Error("Invalid stored form option");
    }
    return value;
}

function validateOptionalString(value) {
    if (value !== undefined) {
        validateString(value);
    }
    return value || "";
}

function getStoredSubmit() {
    let raw;
    try {
        raw = localStorage.getItem(storageKey);
    } catch (e) {
        return null;
    }
    if (!raw) {
        return null;
    }

    try {
        const data = JSON.parse(raw);
        if (!data || !Array.isArray(data.lifts)) {
            throw new Error("Invalid stored form schema");
        }
        validateEnum(data.gender, ["male", "female"]);
        validateEnum(data.bodyweight_unit, ["kg", "lb", "st"]);
        validateString(data.age);
        validateString(data.bodyweight);
        validateString(data.bodyweight_minor);
        if (data.lifts.length > 50) {
            throw new Error("Invalid stored lift count");
        }
        for (let i = 0; i < data.lifts.length; i++) {
            validateString(data.lifts[i].exercise_id);
            validateString(data.lifts[i].exercise_name);
            validateString(data.lifts[i].exercise_category);
            validateOptionalString(data.lifts[i].weight);
            if (data.lifts[i].weight_unit !== undefined) {
                validateEnum(data.lifts[i].weight_unit, ["kg", "lb"]);
            }
            validateString(data.lifts[i].reps);
        }
        return data;
    } catch (e) {
        try {
            localStorage.removeItem(storageKey);
        } catch (ignore) {}
        throw e;
    }
}

function storeSubmit(form, rows) {
    try {
        const data = {
            gender: getInputValue(form, "[name='gender']"),
            age: getInputValue(form, "[name='age']"),
            bodyweight: getInputValue(form, "[name='bodyweight']"),
            bodyweight_minor: getInputValue(form, "[name='bodyweight_minor']"),
            bodyweight_unit: getInputValue(form, "[name='bodyweight_unit']"),
            lifts: getRows(rows).map(function (row) {
                const lift = {
                    exercise_id: getInputValue(row, "[data-symmetry-exercise-hidden]"),
                    exercise_name: row.querySelector("[data-symmetry-exercise-name]") ? row.querySelector("[data-symmetry-exercise-name]").textContent || "" : "",
                    exercise_category: row.dataset.symmetryExerciseCategory || "",
                    reps: getInputValue(row, "[data-symmetry-reps]"),
                };
                if (!isBodyweightCategory(lift.exercise_category)) {
                    lift.weight = getInputValue(row, "[data-symmetry-weight]");
                    lift.weight_unit = getInputValue(row, "[data-symmetry-weight-unit]");
                }
                return lift;
            }),
        };
        localStorage.setItem(storageKey, JSON.stringify(data));
    } catch (e) {}
}

function updateBodyweightMinor(form) {
    const bodyweightUnitSelect = form.querySelector("[name='bodyweight_unit']");
    const bodyweightInput = form.querySelector("[name='bodyweight']");
    const bodyweightMinorInput = form.querySelector("[name='bodyweight_minor']");
    const bodyweightMinorContainer = document.getElementById("bodyweightminorcontainer");
    const bodyweightMinorUnitContainer = document.getElementById("bodyweightminorunitcontainer");
    const bodyweightMinorUnit = document.getElementById("bodyweightminorunit");
    const unit = unitDefinitions[bodyweightUnitSelect ? bodyweightUnitSelect.value : "lb"] || unitDefinitions.lb;
    const hasRemainder = !!unit.remainderSymbol;

    if (bodyweightInput) {
        bodyweightInput.step = unit.step;
    }
    if (bodyweightMinorInput) {
        bodyweightMinorInput.disabled = !hasRemainder;
        bodyweightMinorInput.required = hasRemainder;
        bodyweightMinorInput.step = unit.step;
        bodyweightMinorInput.max = hasRemainder ? unit.remainderMultiple - unit.step : "";
    }
    if (bodyweightMinorContainer) {
        bodyweightMinorContainer.classList.toggle("is-hidden", !hasRemainder);
    }
    if (bodyweightMinorUnitContainer) {
        bodyweightMinorUnitContainer.classList.toggle("is-hidden", !hasRemainder);
    }
    if (bodyweightMinorUnit && hasRemainder) {
        bodyweightMinorUnit.textContent = bodyweightMinorUnit.dataset[unit.remainderSymbol + "Label"] || unit.remainderSymbol;
    }
}

export function initStrengthSymmetryCalculator(config) {
    config = config || {};

    const form = document.getElementById("strengthSymmetryForm");
    const rows = document.querySelector("[data-symmetry-lifts]");
    const template = document.getElementById("strength-symmetry-row-template");
    if (!form || !rows || !template) {
        return;
    }

    const exercises = config.exercises || [];
    const bodyweightUnitSelect = form.querySelector("[name='bodyweight_unit']");
    const initialRowsHtml = rows.innerHTML;
    const initialNextIndex = rows.querySelectorAll("[data-lift-row]").length;
    let nextIndex = initialNextIndex;

    const exerciseModal = createExerciseModal({
        root: "#strengthSymmetryExerciseModal",
        initialExercises: findPrefillResponse("/api/exercises"),
        onSelect: function (exercise) {
            addRow(exercise);
        },
    });

    function createRow(index) {
        const wrapper = document.createElement("div");
        wrapper.innerHTML = template.innerHTML.replace(/__INDEX__/g, String(index));
        return wrapper.querySelector("[data-lift-row]");
    }

    function addRow(exercise) {
        if (exercise && hasExerciseRow(rows, exercise.id)) {
            return;
        }
        const row = createRow(nextIndex++);
        const unitSelect = row.querySelector("[data-symmetry-weight-unit]");
        if (unitSelect) {
            unitSelect.value = getPageLiftUnit(rows, bodyweightUnitSelect);
        }
        rows.appendChild(row);
        enhanceRow(row, exercises);
        if (exercise) {
            setExercise(row, exercise);
        }
    }

    function resetLiftRows() {
        rows.innerHTML = initialRowsHtml;
        nextIndex = initialNextIndex;
        updateBodyweightMinor(form);
        enhanceRows(rows, exercises);
    }

    function resetFormToInitial() {
        form.reset();
        resetLiftRows();
    }

    function restoreStoredSubmit() {
        const data = getStoredSubmit();
        if (!data) {
            return;
        }

        setSelectValue(form, "[name='gender']", data.gender);
        setInputValue(form, "[name='age']", data.age);
        setInputValue(form, "[name='bodyweight']", data.bodyweight);
        setSelectValue(form, "[name='bodyweight_unit']", data.bodyweight_unit);
        updateBodyweightMinor(form);
        setInputValue(form, "[name='bodyweight_minor']", data.bodyweight_minor);

        rows.innerHTML = "";
        nextIndex = 0;
        const restoredExerciseIds = {};
        let restoredUnit = null;
        for (let i = 0; i < data.lifts.length; i++) {
            if (data.lifts[i].exercise_id && restoredExerciseIds[data.lifts[i].exercise_id]) {
                continue;
            }
            restoredExerciseIds[data.lifts[i].exercise_id] = true;
            restoredUnit = restoredUnit || data.lifts[i].weight_unit || null;
            const row = createRow(nextIndex++);
            setInputValue(row, "[data-symmetry-exercise-hidden]", data.lifts[i].exercise_id);
            const label = row.querySelector("[data-symmetry-exercise-name]");
            if (label) {
                label.textContent = data.lifts[i].exercise_name;
            }
            row.dataset.symmetryExerciseCategory = data.lifts[i].exercise_category;
            setInputValue(row, "[data-symmetry-weight]", data.lifts[i].weight || "");
            if (data.lifts[i].weight_unit) {
                setSelectValue(row, "[data-symmetry-weight-unit]", data.lifts[i].weight_unit);
            }
            setInputValue(row, "[data-symmetry-reps]", data.lifts[i].reps);
            updateRowForExercise(row);
            rows.appendChild(row);
        }
        if (restoredUnit) {
            setAllWeightUnits(rows, restoredUnit);
        }
        enhanceRows(rows, exercises);
    }

    const addLift = document.querySelector("[data-add-lift]");
    if (addLift) {
        addLift.addEventListener("click", function () {
            if (exerciseModal) {
                exerciseModal.showModal();
            }
        });
    }

    const clearLifts = document.querySelector("[data-clear-lifts]");
    if (clearLifts) {
        clearLifts.addEventListener("click", function () {
            if (!window.confirm("Clear all exercises?")) {
                return;
            }
            rows.innerHTML = "";
            nextIndex = 0;
        });
    }

    if (bodyweightUnitSelect) {
        bodyweightUnitSelect.addEventListener("change", function () {
            if ((bodyweightUnitSelect.value === "kg" || bodyweightUnitSelect.value === "lb") && !hasAnyLiftWeight(rows)) {
                setAllWeightUnits(rows, bodyweightUnitSelect.value);
            }
            updateBodyweightMinor(form);
        });
    }

    rows.addEventListener("change", function (event) {
        if (!event.target.closest("[data-symmetry-weight-unit]")) {
            return;
        }
        setAllWeightUnits(rows, event.target.value);
    });

    rows.addEventListener("click", function (event) {
        if (!event.target.closest("[data-remove-lift]")) {
            return;
        }
        const row = event.target.closest("[data-lift-row]");
        if (row) {
            row.parentNode.removeChild(row);
        }
    });

    const resetButton = form.querySelector("[data-reset-form]");
    if (resetButton) {
        resetButton.addEventListener("click", function () {
            if (window.confirm("Reset the form?")) {
                resetFormToInitial();
            }
        });
    }

    form.addEventListener("submit", function () {
        storeSubmit(form, rows);
    });

    updateBodyweightMinor(form);
    enhanceRows(rows, exercises);
    try {
        restoreStoredSubmit();
    } catch (e) {
        resetFormToInitial();
    }
}
initStrengthSymmetryCalculator(window.strengthLevelSymmetryCalculator || {});
setupTabs();
